import { toast } from "sonner";
import { useContext } from "react";
import { CrmProvider, AuthMethod, CrmFormData } from "../types";
import AuthMethodSelector from "../AuthMethodSelector";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type SalesforceAuthConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
  hasConnection: boolean;
  crmConnection: any;
};

const SalesforceAuthConfiguration = ({
  formData,
  onFormDataChange,
  hasConnection,
  crmConnection,
}: SalesforceAuthConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const utils = trpc.useUtils();
  const createConnection = trpc.crmSelfConfig.create.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la conexión.",
      });
    },
  });

  const testConnection = trpc.crmSelfConfig.testConnection.useMutation({
    onSuccess: async (result) => {
      if (result.success) {
        const instanceUrl = formData.instanceUrl || crmConnection?.salesforce?.instanceUrl;
        const clientId = formData.clientId || crmConnection?.salesforce?.clientId;
        const clientSecret = formData.clientSecret || crmConnection?.salesforce?.clientSecret;

        if (merchantId && instanceUrl && clientId && clientSecret) {
          await createConnection.mutateAsync({
            merchantId,
            provider: "salesforce" as CrmProvider,
            salesforce: {
              clientId: clientId || "",
              clientSecret: clientSecret || "",
              accessToken: result.accessToken,
              instanceUrl: instanceUrl,
            },
            status: "success",
            enabled: true,
          });
        }

        toast.success("Conexión exitosa", {
          description: "La conexión con el CRM se ha establecido correctamente.",
        });
      } else {
        toast.error("Error de conexión", {
          description: result.error || "No se pudo establecer la conexión con el CRM.",
        });
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al probar la conexión.",
      });
    },
  });

  const handleTestConnection = () => {
    const instanceUrl = (
      formData.instanceUrl ||
      crmConnection?.salesforce?.instanceUrl ||
      ""
    ).trim();
    const clientId = (formData.clientId || crmConnection?.salesforce?.clientId || "").trim();
    const clientSecret = (
      formData.clientSecret ||
      crmConnection?.salesforce?.clientSecret ||
      ""
    ).trim();

    if (!instanceUrl || !clientId || !clientSecret) {
      toast.warning("Credenciales requeridas", {
        description:
          "Por favor ingresa todos los campos (URL de instancia, Client ID y Client Secret) antes de probar la conexión.",
      });
      return;
    }

    testConnection.mutate({
      provider: "salesforce" as CrmProvider,
      instanceUrl: instanceUrl,
      clientId: clientId,
      clientSecret: clientSecret,
    });
  };

  const handleAuthMethodChange = (method: AuthMethod) => {
    onFormDataChange({
      authMethod: method,
      instanceUrl: formData.authMethod === method ? formData.instanceUrl : "",
      clientId: formData.authMethod === method ? formData.clientId : "",
      clientSecret: formData.authMethod === method ? formData.clientSecret : "",
    });
  };

  const handleInstanceUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ instanceUrl: e.target.value });
  };

  const handleClientIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ clientId: e.target.value });
  };

  const handleClientSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ clientSecret: e.target.value });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-lg font-bold text-gray-700">
              Método de autenticación
            </Label>
            {hasConnection && crmConnection.status === "success" && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Conexión exitosa
              </span>
            )}
          </div>
          <AuthMethodSelector
            methods={["oauth"]}
            selectedMethod={
              hasConnection && crmConnection.status === "success"
                ? "oauth"
                : formData.authMethod || "oauth"
            }
            onSelect={handleAuthMethodChange}
            isDisabled={hasConnection && crmConnection.status === "success"}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            URL de instancia
          </Label>
          <Input
            type="text"
            value={formData.instanceUrl || crmConnection?.salesforce?.instanceUrl || ""}
            onChange={handleInstanceUrlChange}
            placeholder="https://tuempresa.my.salesforce.com"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={hasConnection && crmConnection.status === "success"}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Client ID
          </Label>
          <Input
            type="text"
            value={
              hasConnection &&
              crmConnection.status === "success" &&
              crmConnection.salesforce?.clientId
                ? "••••••••••••"
                : formData.clientId || crmConnection?.salesforce?.clientId || ""
            }
            onChange={handleClientIdChange}
            placeholder="Ingresa el Client ID"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={hasConnection && crmConnection.status === "success"}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Client Secret
          </Label>
          <Input
            type="password"
            value={
              hasConnection &&
              crmConnection.status === "success" &&
              crmConnection.salesforce?.clientSecret
                ? "••••••••••••"
                : formData.clientSecret || crmConnection?.salesforce?.clientSecret || ""
            }
            onChange={handleClientSecretChange}
            placeholder="Ingresa el Client Secret"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={hasConnection && crmConnection.status === "success"}
          />
        </div>

        {!(hasConnection && crmConnection.status === "success") && (
          <Button
            variant="main"
            onClick={handleTestConnection}
            disabled={
              testConnection.isLoading ||
              (!formData.instanceUrl && !crmConnection?.salesforce?.instanceUrl) ||
              (!formData.clientId && !crmConnection?.salesforce?.clientId) ||
              (!formData.clientSecret && !crmConnection?.salesforce?.clientSecret)
            }
          >
            {testConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
            Probar conexión
          </Button>
        )}
      </div>
    </div>
  );
};

export default SalesforceAuthConfiguration;
